/**
 * MetricCard
 *
 * Enhanced card for displaying headline metrics with context.
 * Supports trend indicators and secondary metrics.
 */
import React from 'react';
import {
  Card,
  makeStyles,
  tokens,
  Body2,
} from '@fluentui/react-components';
import {
  ArrowUp16Regular,
  ArrowDown16Regular,
  Subtract16Regular,
} from '@fluentui/react-icons';
import { semanticColors } from '../../theme/solution-theme';

interface SecondaryMetric {
  label: string;
  value: string | number;
}

interface TrendInfo {
  direction: 'up' | 'down' | 'stable';
  label: string;
  sentiment: 'positive' | 'negative' | 'neutral';
}

export interface MetricCardProps {
  title: string;
  value: string | number;
  unit?: string;
  secondaryMetrics?: SecondaryMetric[];
  trend?: TrendInfo;
  icon?: React.ReactNode;
  accentColor?: string;
}

const sentimentColors: Record<string, string> = {
  positive: semanticColors.success,
  negative: semanticColors.error,
  // #94A3B8 (slate grey) is not in brand-config.json's palette — left as-is.
  neutral: '#94A3B8',
};

const useStyles = makeStyles({
  card: {
    padding: tokens.spacingVerticalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  icon: {
    fontSize: tokens.fontSizeBase500,
    display: 'flex',
  },
  valueRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: tokens.spacingHorizontalS,
  },
  value: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightBold,
    lineHeight: '1',
    color: tokens.colorNeutralForeground1,
  },
  unit: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
  },
  trend: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    fontSize: tokens.fontSizeBase200,
  },
  secondaryMetrics: {
    display: 'flex',
    gap: tokens.spacingHorizontalL,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingTop: tokens.spacingVerticalS,
  },
  secondaryMetric: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  secondaryLabel: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  secondaryValue: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
  },
});

const TrendIcon: React.FC<{ direction: TrendInfo['direction'] }> = ({ direction }) => {
  switch (direction) {
    case 'up':
      return <ArrowUp16Regular />;
    case 'down':
      return <ArrowDown16Regular />;
    case 'stable':
      return <Subtract16Regular />;
  }
};

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  unit,
  secondaryMetrics,
  trend,
  icon,
  accentColor,
}) => {
  const styles = useStyles();

  return (
    <Card className={styles.card}>
      <div className={styles.header}>
        <span className={styles.title}>{title}</span>
        {icon && (
          <span className={styles.icon} style={accentColor ? { color: accentColor } : undefined}>
            {icon}
          </span>
        )}
      </div>

      <div className={styles.valueRow}>
        <span className={styles.value} style={accentColor ? { color: accentColor } : undefined}>
          {value}
        </span>
        {unit && <span className={styles.unit}>{unit}</span>}
      </div>

      {trend && (
        <div className={styles.trend} style={{ color: sentimentColors[trend.sentiment] }}>
          <TrendIcon direction={trend.direction} />
          <Body2 style={{ color: 'inherit' }}>{trend.label}</Body2>
        </div>
      )}

      {secondaryMetrics && secondaryMetrics.length > 0 && (
        <div className={styles.secondaryMetrics}>
          {secondaryMetrics.map((metric) => (
            <div key={metric.label} className={styles.secondaryMetric}>
              <span className={styles.secondaryLabel}>{metric.label}</span>
              <span className={styles.secondaryValue}>{metric.value}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
